import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiHandler } from '../../api/apihandler';
import { 
  MEDICATION_ADMINISTRATION_LIST, 
  MEDICATION_ADMINISTRATION_FORM, 
  MEDICATION_ADMINISTRATION_LOGS,
  MEDICATION_ADMINISTRATION_DELETE 
} from '../../api/constants';

// Async thunk for fetching medication administration list
export const fetchMedicationList = createAsyncThunk(
  'medication/fetchMedicationList',
  async ({ type = 1, page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const response = await apiHandler(MEDICATION_ADMINISTRATION_LIST, "POST", {
        type,
        page,
        limit
      });
      
      if (response.data?.response?.status === 200) {
        return { ...response.data.data.formDatas, type };
      } else {
        return rejectWithValue(response.data?.response?.message || 'Failed to fetch medication list');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Network error');
    }
  }
);

// Async thunk for submitting medication administration form
export const submitMedicationForm = createAsyncThunk(
  'medication/submitMedicationForm',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await apiHandler(MEDICATION_ADMINISTRATION_FORM, "POST", formData);
      
      if (response.data?.response?.status === 200) {
        return response.data.data;
      } else {
        return rejectWithValue(response.data?.response?.message || 'Failed to submit form');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Network error');
    }
  }
);

// Async thunk for fetching medication logs
export const fetchMedicationLogs = createAsyncThunk(
  'medication/fetchMedicationLogs',
  async ({ administrationId, type = 1 }, { rejectWithValue }) => {
    try {
      // The backend defaults `type` to 1 (Medication) when it's missing from
      // the request - never sending it here meant the Non-Medication Logs
      // page always queried for Medication-type rows against this
      // administration_medication_id, which never matches a Non-Medication
      // log, so it always came back empty regardless of what staff logged.
      const response = await apiHandler(MEDICATION_ADMINISTRATION_LOGS, "POST", {
        administration_medication_id: administrationId,
        type
      });
      
      if (response.data?.response?.status === 200) {
        return response.data.data.formDatas.data;
      } else {
        return rejectWithValue(response.data?.response?.message || 'Failed to fetch logs');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Network error');
    }
  }
);

// Async thunk for deleting medication
export const deleteMedication = createAsyncThunk(
  'medication/deleteMedication',
  async ({ id, type }, { rejectWithValue }) => {
    try {
      const response = await apiHandler(MEDICATION_ADMINISTRATION_DELETE, "POST", {
        id
      });
      
      if (response.data?.response?.status === 200) {
        return { id, type, message: response.data.data.message };
      } else {
        return rejectWithValue(response.data?.response?.message || 'Failed to delete medication');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Network error');
    }
  }
);

const medicationSlice = createSlice({
  name: 'medication',
  initialState: {
    // Lists
    medicationList: [],
    nonMedicationList: [],
    
    // Logs
    medicationLogs: [],
    
    // Loading states
    listLoading: false,
    formSubmitting: false,
    logsLoading: false,
    deleteLoading: false,
    
    // Error states
    listError: null,
    formError: null,
    logsError: null,
    deleteError: null,
    
    // Meta data
    medicationMeta: {
      total: 0,
      per_page: 10,
      current_page: 1,
      last_page: 1
    },
    nonMedicationMeta: {
      total: 0,
      per_page: 10,
      current_page: 1,
      last_page: 1
    },
    
    // Form success message
    formSuccessMessage: null
  },
  reducers: {
    clearErrors: (state) => {
      state.listError = null;
      state.formError = null;
      state.logsError = null;
    },
    clearFormSuccess: (state) => {
      state.formSuccessMessage = null;
    },
    resetMedicationList: (state) => {
      state.medicationList = [];
      state.medicationMeta = {
        total: 0,
        per_page: 10,
        current_page: 1,
        last_page: 1
      };
    },
    resetNonMedicationList: (state) => {
      state.nonMedicationList = [];
      state.nonMedicationMeta = {
        total: 0,
        per_page: 10,
        current_page: 1,
        last_page: 1
      };
    },
    resetLogs: (state) => {
      state.medicationLogs = [];
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch medication list
      .addCase(fetchMedicationList.pending, (state) => {
        state.listLoading = true;
        state.listError = null;
      })
      .addCase(fetchMedicationList.fulfilled, (state, action) => {
        state.listLoading = false;
        const { data, meta, type } = action.payload;
        
        if (type === 1) {
          // Medication
          state.medicationList = data;
          state.medicationMeta = meta;
        } else {
          // Non-medication
          state.nonMedicationList = data;
          state.nonMedicationMeta = meta;
        }
      })
      .addCase(fetchMedicationList.rejected, (state, action) => {
        state.listLoading = false;
        state.listError = action.payload;
      })
      
      // Submit medication form
      .addCase(submitMedicationForm.pending, (state) => {
        state.formSubmitting = true;
        state.formError = null;
        state.formSuccessMessage = null;
      })
      .addCase(submitMedicationForm.fulfilled, (state, action) => {
        state.formSubmitting = false;
        state.formSuccessMessage = 'Form submitted successfully!';
      })
      .addCase(submitMedicationForm.rejected, (state, action) => {
        state.formSubmitting = false;
        state.formError = action.payload;
      })
      
      // Fetch medication logs
      .addCase(fetchMedicationLogs.pending, (state) => {
        state.logsLoading = true;
        state.logsError = null;
      })
      .addCase(fetchMedicationLogs.fulfilled, (state, action) => {
        state.logsLoading = false;
        state.medicationLogs = action.payload;
      })
      .addCase(fetchMedicationLogs.rejected, (state, action) => {
        state.logsLoading = false;
        state.logsError = action.payload;
      })
      
      // Delete medication
      .addCase(deleteMedication.pending, (state) => {
        state.deleteLoading = true;
        state.deleteError = null;
      })
      .addCase(deleteMedication.fulfilled, (state, action) => {
        state.deleteLoading = false;
        // Remove deleted item from appropriate list
        if (action.payload.type === 1) {
          state.medicationList = state.medicationList.filter(
            med => med.id !== action.payload.id
          );
          state.medicationMeta.total -= 1;
        } else {
          state.nonMedicationList = state.nonMedicationList.filter(
            med => med.id !== action.payload.id
          );
          state.nonMedicationMeta.total -= 1;
        }
      })
      .addCase(deleteMedication.rejected, (state, action) => {
        state.deleteLoading = false;
        state.deleteError = action.payload;
      });
  }
});

export const { 
  clearErrors, 
  clearFormSuccess, 
  resetMedicationList, 
  resetNonMedicationList, 
  resetLogs 
} = medicationSlice.actions;

// Selectors
export const selectMedicationList = (state) => state.medication.medicationList;
export const selectNonMedicationList = (state) => state.medication.nonMedicationList;
export const selectMedicationLogs = (state) => state.medication.medicationLogs;

export const selectListLoading = (state) => state.medication.listLoading;
export const selectFormSubmitting = (state) => state.medication.formSubmitting;
export const selectLogsLoading = (state) => state.medication.logsLoading;
export const selectDeleteLoading = (state) => state.medication.deleteLoading;

export const selectListError = (state) => state.medication.listError;
export const selectFormError = (state) => state.medication.formError;
export const selectLogsError = (state) => state.medication.logsError;
export const selectDeleteError = (state) => state.medication.deleteError;

export const selectMedicationMeta = (state) => state.medication.medicationMeta;
export const selectNonMedicationMeta = (state) => state.medication.nonMedicationMeta;

export const selectFormSuccessMessage = (state) => state.medication.formSuccessMessage;

// Selector to find medication by ID from list
export const selectMedicationById = (id) => (state) => {
  return state.medication.medicationList.find(med => med.id === parseInt(id));
};

// Selector to find non-medication by ID from list
export const selectNonMedicationById = (id) => (state) => {
  return state.medication.nonMedicationList.find(med => med.id === parseInt(id));
};

export default medicationSlice.reducer;